import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import type { Schema } from "@bw/shared/types/generated";

type AdminCandidate = Schema["PlatformAdminAccessCandidate"];

export type EligibleAdminCandidate = AdminCandidate & { firebase_uid: string };

export type DialogTarget = Readonly<{
  alertId: string;
  kind: "assign" | "mute";
  title: string;
  token: number;
}>;

type CandidateIdentity = Readonly<{
  accessibleName: string;
  detail?: string;
  primary: string;
  selectable: boolean;
}>;

export const MUTE_WINDOWS: ReadonlyArray<{
  label: string;
  minutes: number;
}> = [
  { label: "1 hour", minutes: 60 },
  { label: "4 hours", minutes: 240 },
  { label: "24 hours", minutes: 1440 },
];

export function isEligibleCandidate(
  candidate: AdminCandidate,
): candidate is EligibleAdminCandidate {
  return (
    candidate.access_state === "already_admin" &&
    candidate.is_platform_admin &&
    !candidate.suspended &&
    typeof candidate.firebase_uid === "string" &&
    candidate.firebase_uid.trim().length > 0
  );
}

function safeEmail(value: string | null | undefined): string | null {
  const email = value?.trim() ?? "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function safeName(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

export function duplicateCandidateNames(
  candidates: readonly EligibleAdminCandidate[],
): ReadonlySet<string> {
  const counts = new Map<string, number>();
  for (const candidate of candidates) {
    const name = safeName(candidate.display_name)?.toLocaleLowerCase();
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return new Set(
    [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([name]) => name),
  );
}

export function candidateIdentity(
  candidate: AdminCandidate,
  duplicateNames: ReadonlySet<string>,
): CandidateIdentity {
  const name = safeName(candidate.display_name);
  const email = safeEmail(candidate.email);

  if (!name && !email) {
    return {
      accessibleName: "Identity unavailable",
      primary: "Identity unavailable",
      selectable: false,
    };
  }

  if (!name) {
    return {
      accessibleName: `Select ${email!}`,
      primary: email!,
      selectable: true,
    };
  }

  if (!duplicateNames.has(name.toLocaleLowerCase())) {
    return {
      accessibleName: `Select ${name}`,
      primary: name,
      selectable: true,
    };
  }

  if (!email) {
    return {
      accessibleName: "Identity unavailable",
      detail: "Identity unavailable",
      primary: name,
      selectable: false,
    };
  }

  return {
    accessibleName: `Select ${name} ${email}`,
    detail: email,
    primary: name,
    selectable: true,
  };
}

export function AssignAlertDialog({
  actionError,
  actionErrorRefreshing,
  actionLocked,
  actionPending,
  assignmentReady,
  candidates,
  duplicateNames,
  open,
  rosterError,
  rosterFetching,
  rosterPending,
  selectedAssignee,
  target,
  onAssign,
  onClose,
  onRefreshActionError,
  onRetryRoster,
  onSelectAssignee,
}: {
  actionError: string | null;
  actionErrorRefreshing: boolean;
  actionLocked: boolean;
  actionPending: boolean;
  assignmentReady: boolean;
  candidates: readonly EligibleAdminCandidate[];
  duplicateNames: ReadonlySet<string>;
  open: boolean;
  rosterError: boolean;
  rosterFetching: boolean;
  rosterPending: boolean;
  selectedAssignee: string | null;
  target: DialogTarget | null;
  onAssign: () => void;
  onClose: () => void;
  onRefreshActionError: (() => void) | null;
  onRetryRoster: () => void;
  onSelectAssignee: (firebaseUid: string) => void;
}) {
  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={() => onClose()}>
      <DialogTitle>Assign alert</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            Choose the current platform admin responsible for{" "}
            {target?.kind === "assign" ? `“${target.title}”.` : "this alert."}
          </Typography>

          {actionPending ? (
            <Box
              role="status"
              aria-label="Assigning alert"
              aria-live="polite"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={18} />
              <Typography color="text.secondary" variant="body2">
                Assigning alert
              </Typography>
            </Box>
          ) : null}

          {actionError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                onRefreshActionError ? (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={actionErrorRefreshing || actionPending}
                    onClick={onRefreshActionError}
                  >
                    Refresh
                  </Button>
                ) : undefined
              }
            >
              {actionError}
            </Alert>
          ) : null}

          {rosterPending ? (
            <Box
              role="status"
              aria-label="Loading assignment roster"
              aria-live="polite"
              sx={{ display: "flex", justifyContent: "center", py: 4 }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : rosterError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={rosterFetching || actionPending}
                  onClick={onRetryRoster}
                >
                  Retry
                </Button>
              }
            >
              The assignment roster could not be loaded.
            </Alert>
          ) : candidates.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No platform admins are available to assign.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <List disablePadding>
                {candidates.map((candidate, index) => {
                  const identity = candidateIdentity(candidate, duplicateNames);
                  const uid = candidate.firebase_uid.trim();
                  return (
                    <ListItem
                      key={uid}
                      disablePadding
                      divider={index < candidates.length - 1}
                    >
                      <ListItemButton
                        component="button"
                        type="button"
                        selected={selectedAssignee === uid}
                        aria-label={identity.accessibleName}
                        disabled={actionLocked || !identity.selectable}
                        onClick={() => onSelectAssignee(uid)}
                      >
                        <ListItemText
                          primary={identity.primary}
                          secondary={identity.detail}
                          slotProps={{
                            primary: { sx: { fontWeight: 600 } },
                            secondary: { variant: "caption" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" disabled={actionLocked} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={actionLocked || !assignmentReady}
          onClick={onAssign}
        >
          {actionPending ? "Assigning…" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MuteAlertDialog({
  actionError,
  actionErrorRefreshing,
  actionLocked,
  actionPending,
  actionReady,
  minutes,
  open,
  target,
  onClose,
  onMinutesChange,
  onMute,
  onRefreshActionError,
}: {
  actionError: string | null;
  actionErrorRefreshing: boolean;
  actionLocked: boolean;
  actionPending: boolean;
  actionReady: boolean;
  minutes: number;
  open: boolean;
  target: DialogTarget | null;
  onClose: () => void;
  onMinutesChange: (minutes: number) => void;
  onMute: () => void;
  onRefreshActionError: (() => void) | null;
}) {
  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={() => onClose()}>
      <DialogTitle>Mute alert</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            Choose how long to mute{" "}
            {target?.kind === "mute" ? `“${target.title}”.` : "this alert."}
          </Typography>
          {actionPending ? (
            <Box
              role="status"
              aria-label="Muting alert"
              aria-live="polite"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={18} />
              <Typography color="text.secondary" variant="body2">
                Muting alert
              </Typography>
            </Box>
          ) : null}
          {actionError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                onRefreshActionError ? (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={actionErrorRefreshing || actionPending}
                    onClick={onRefreshActionError}
                  >
                    Refresh
                  </Button>
                ) : undefined
              }
            >
              {actionError}
            </Alert>
          ) : null}
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            aria-label="Mute window"
            value={minutes}
            disabled={actionLocked}
            onChange={(_event, value: number | null) => {
              if (value) onMinutesChange(value);
            }}
          >
            {MUTE_WINDOWS.map((window) => (
              <ToggleButton key={window.minutes} value={window.minutes}>
                {window.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" disabled={actionLocked} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={actionLocked || !actionReady}
          onClick={onMute}
        >
          {actionPending ? "Muting…" : "Mute"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
